import apiService from '../Api'
import {
  ICourseThemeColor,
  IScheduleClass,
  IScheduleClassFilter,
  IScheduleClassListResponse,
  IScheduleClassPayload,
  IScheduleClassSettings,
  IScheduleFormOptions,
  IScheduleTermCounts,
  TScheduleStatus,
  TScheduleTerm
} from './interfaces'

export default class ScheduleClassService {
  async list(year: number, term: TScheduleTerm, filter: IScheduleClassFilter): Promise<IScheduleClassListResponse> {
    const params = new URLSearchParams()
    params.append('year', year.toString())
    params.append('term', term)
    params.append('page', filter.page.toString())
    params.append('page_size', filter.page_size.toString())
    if (filter.search) params.append('search', filter.search)
    if (filter.course_id !== '') params.append('course_id', filter.course_id.toString())
    if (filter.teacher_id !== '') params.append('teacher_id', filter.teacher_id.toString())
    filter.status.forEach((status) => params.append('status[]', status))
    if (filter.shift !== '') params.append('shift', filter.shift)
    filter.days.forEach((day) => params.append('days[]', day))
    if (filter.format !== '') params.append('format', filter.format)
    // Enviado como '1' e não como boolean: o paramsSerializer do Api.ts serializa objetos/booleans
    // de forma própria e um boolean cru chegaria como "true"/"false".
    if (filter.only_incomplete) params.append('only_incomplete', '1')
    if (filter.only_upcoming) params.append('only_upcoming', '1')

    return apiService.get('/schedule-classes', { params }).then((r) => r.data)
  }

  async create(data: IScheduleClassPayload): Promise<{ message: string; data: IScheduleClass }> {
    return apiService.post('/schedule-classes', data).then((r) => r.data)
  }

  async update(id: number, data: IScheduleClassPayload): Promise<{ message: string; data: IScheduleClass }> {
    return apiService.put(`/schedule-classes/${id}`, data).then((r) => r.data)
  }

  /**
   * Edição rápida pela listagem. Campo único de propósito: o PUT exige a turma inteira, e
   * reenviá-la a cada troca de status sobrescreveria o que outra pessoa tivesse editado.
   *
   * Devolve o status GRAVADO, que pode não ser o enviado: o backend confirma ou esgota a turma
   * conforme a ocupação (ABRA-315). Repintar o badge com o valor pedido mostraria "Pendente"
   * numa turma que o servidor deixou como "Vagas Esgotadas", até o próximo carregamento.
   */
  async updateStatus(id: number, status: TScheduleStatus): Promise<TScheduleStatus> {
    const response = await apiService.patch(`/schedule-classes/${id}/status`, { status })

    return response.data.data.status
  }

  async remove(id: number): Promise<void> {
    await apiService.delete(`/schedule-classes/${id}`)
  }

  /** Anos já cadastrados unidos ao ano atual e aos 2 seguintes, em ordem decrescente. */
  async years(): Promise<number[]> {
    return apiService.get('/schedule-classes/years').then((r) => r.data)
  }

  /** Contadores por período do ano, para os badges das abas (sem os filtros da tela). */
  async termCounts(year: number): Promise<IScheduleTermCounts> {
    return apiService.get('/schedule-classes/counts', { params: { year } }).then((r) => r.data)
  }

  async formOptions(): Promise<IScheduleFormOptions> {
    return apiService.get('/schedule-classes/form-options').then((r) => r.data)
  }

  async getSettings(): Promise<IScheduleClassSettings> {
    return apiService.get('/schedule-classes/settings').then((r) => r.data)
  }

  /** Devolve o valor GRAVADO, que é o que o switch deve mostrar depois. */
  async updateSettings(settings: IScheduleClassSettings): Promise<IScheduleClassSettings> {
    return apiService.put('/schedule-classes/settings', settings).then((r) => r.data)
  }

  async listThemeColors(): Promise<ICourseThemeColor[]> {
    return apiService.get('/courses/theme-colors').then((r) => r.data.data)
  }

  /** Enviar null restaura o curso para a cor padrão da marca. */
  async updateThemeColor(courseId: number, themeColor: string | null): Promise<ICourseThemeColor> {
    return apiService.patch(`/courses/${courseId}/theme-color`, { theme_color: themeColor }).then((r) => r.data.data)
  }
}
